/**
 * <ConversationPanel> — le fil et, dessous, de quoi répondre (organism).
 *
 * L'assemblage que la feuille de Sam et l'écran de la messagerie habillent chacun à sa façon :
 * le fil prend toute la hauteur qui reste, et le pied — un bandeau éventuel, les suggestions
 * rapides, le composeur — tient au-dessus du clavier. Il ne dessine ni en-tête ni fond : la
 * feuille ou la page qui le porte les possède.
 *
 * Pure composition : le fil, les suggestions et le composeur gardent chacun leurs props.
 */
import React from "react"
import { KeyboardAvoidingView, Platform, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { MessageComposer, type MessageComposerProps } from "../../molecules/MessageComposer"
import { QuickReplies, type QuickRepliesProps } from "../../molecules/QuickReplies"
import { CONVERSATION_MAX_WIDTH, ConversationThread, type ConversationThreadProps } from "../ConversationThread"

export type ConversationPanelProps = {
    thread: ConversationThreadProps
    /**
     * De quoi répondre. Absent : un fil où l'on ne fait que lire (« Tous » du live, où seul le
     * tuteur publie) ; le pied ne porte alors que le bandeau (`notice`).
     */
    composer?: MessageComposerProps
    /** Les suggestions rapides, au-dessus du composeur. Absentes : pas de rangée. */
    quickReplies?: QuickRepliesProps
    /** Un bandeau entre le fil et le composeur — le plafond de messages de Sam, sa lenteur. */
    notice?: React.ReactNode
    /**
     * Le pied dégage lui-même l'indicateur d'accueil. Défaut `true` : dans une feuille
     * (`<SamSheet>`), rien d'autre ne le fait. Une page qui pose son `SafeAreaView`, comme
     * `<PageTemplate>`, passe `false` : la réserve ne se compte qu'une fois.
     */
    bottomInset?: boolean
    /**
     * Ce qui est au-dessus du panneau sans être dans son parent : iOS n'en tient pas compte pour céder
     * la place au clavier (l'en-tête de la fiche d'un élève). Défaut 0.
     */
    keyboardOffset?: number
    testID?: string
}

export function ConversationPanel(props: ConversationPanelProps) {
    const { thread, composer, quickReplies, notice, bottomInset = true, keyboardOffset = 0, testID } = props
    const insets = useSafeAreaInsets()

    return (
        <KeyboardAvoidingView
            // Android termine déjà l'app au clavier ; iOS a besoin qu'on lui cède la place.
            behavior={Platform.OS === "ios" ? "padding" : undefined}
            keyboardVerticalOffset={keyboardOffset}
            style={styles.root}
            testID={testID}
        >
            <ConversationThread {...thread} />

            <View style={styles.footer(bottomInset ? insets.bottom : 0)}>
                <View style={styles.column}>
                    {notice}
                    {quickReplies != null && <QuickReplies {...quickReplies} />}
                    {composer != null && <MessageComposer {...composer} />}
                </View>
            </View>
        </KeyboardAvoidingView>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0 },
    footer: (bottomInset: number) => ({
        paddingHorizontal: theme.spacing["4"],
        paddingTop: theme.spacing["2"],
        // Dégage l'indicateur d'accueil, même clavier fermé.
        paddingBottom: Math.max(bottomInset, theme.spacing["3"]),
    }),
    /** La même colonne que le fil : le composeur ne s'étire pas plus large que les bulles. */
    column: {
        width: "100%",
        maxWidth: CONVERSATION_MAX_WIDTH,
        alignSelf: "center",
        gap: theme.spacing["2"],
    },
}))
